import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container, Paper, Title, Text, Button, Stepper, Stack,
  TextInput, Group, ThemeIcon, Box,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { BookOpen, Users, Target, CheckCircle } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import api from '../../api/client'

const TUTOR_STEPS = ['Welcome', 'Quick tour', 'Ready!']
const STUDENT_STEPS = ['Welcome', 'Your goals', 'Quick tour', 'Ready!']

function scoreValidator(v: string | undefined) {
  if (v === undefined || v === '' || v === null) return null
  const n = Number(String(v).trim())
  if (Number.isNaN(n)) return 'Enter a number'
  if (n < 200 || n > 800) return 'SAT section scores are 200–800'
  return null
}

export function OnboardingPage() {
  const { user, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [active, setActive] = useState(0)
  const [saving, setSaving] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const isTutorOrGod = user?.role === 'tutor' || user?.role === 'god'
  const isStudent = user?.role === 'student'
  const steps = isTutorOrGod ? TUTOR_STEPS : STUDENT_STEPS

  const form = useForm({
    initialValues: {
      target_score_rw: '',
      target_score_math: '',
      sat_test_date: '',
    },
    validate: {
      target_score_rw: scoreValidator,
      target_score_math: scoreValidator,
    },
  })

  useEffect(() => {
    if (!user?.onboarding_completed) return
    if (user.role === 'tutor') navigate('/tutor/dashboard', { replace: true })
    else if (user.role === 'god') navigate('/admin/dashboard', { replace: true })
    else navigate('/dashboard', { replace: true })
  }, [user?.onboarding_completed, user?.role, navigate])

  const goalsHydratedFor = useRef<number | null>(null)
  useEffect(() => {
    if (!user) {
      goalsHydratedFor.current = null
      return
    }
    if (!isStudent) return
    if (goalsHydratedFor.current === user.id) return
    goalsHydratedFor.current = user.id
    form.setValues({
      target_score_rw:
        user.target_score_rw != null ? String(user.target_score_rw) : '',
      target_score_math:
        user.target_score_math != null ? String(user.target_score_math) : '',
      sat_test_date: user.sat_date ? String(user.sat_date).slice(0, 10) : '',
    })
  }, [user, isStudent, form])

  const tourActive =
    (isTutorOrGod && active === 1) || (isStudent && active === 2)
  const readyActive =
    (isTutorOrGod && active === 2) || (isStudent && active === 3)

  const handleGoalsSave = async () => {
    const err = form.validate()
    if (err.hasErrors) return

    const rawRw = String(form.values.target_score_rw ?? '').trim()
    const rawMath = String(form.values.target_score_math ?? '').trim()
    const hasRw = rawRw !== ''
    const hasMath = rawMath !== ''
    const hasDate = Boolean(form.values.sat_test_date)

    if (!hasRw && !hasMath && !hasDate) {
      setActive(2)
      return
    }

    const payload: {
      target_score_rw?: number
      target_score_math?: number
      sat_date?: string
    } = {}
    if (hasRw) {
      const n = Number(rawRw)
      if (!Number.isNaN(n)) payload.target_score_rw = n
    }
    if (hasMath) {
      const n = Number(rawMath)
      if (!Number.isNaN(n)) payload.target_score_math = n
    }
    if (hasDate) {
      payload.sat_date = `${form.values.sat_test_date}T12:00:00`
    }

    setSaving(true)
    try {
      await api.put('/auth/me', payload)
      await refreshUser()
    } catch {
      notifications.show({
        title: 'Could not save goals',
        message: 'You can update them later on your dashboard.',
        color: 'yellow',
      })
    } finally {
      setSaving(false)
      setActive(2)
    }
  }

  const skipGoals = () => setActive(2)

  const dashboardPath =
    user?.role === 'tutor'
      ? '/tutor/dashboard'
      : user?.role === 'god'
        ? '/admin/dashboard'
        : '/dashboard'

  const handleFinish = async () => {
    setFinishing(true)
    try {
      await api.put('/auth/me', { onboarding_completed: true })
      await refreshUser()
      notifications.show({ title: 'Welcome!', message: 'Your account is ready.', color: 'green' })
      navigate(dashboardPath)
    } catch {
      notifications.show({
        title: 'Could not finish setup',
        message: 'Try again in a moment.',
        color: 'red',
      })
    } finally {
      setFinishing(false)
    }
  }

  return (
    <Box style={{ minHeight: '100vh', background: 'var(--sat-bg)', display: 'flex', alignItems: 'center', paddingTop: 40, paddingBottom: 40 }}>
      <Container size={680} style={{ width: '100%' }}>
        <Box style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title order={1} style={{ color: 'var(--sat-text)', fontWeight: 800 }}>
            Welcome to SAT Tutor
          </Title>
          <Text c="var(--sat-green)" mt={4}>Let&apos;s get you set up in a few quick steps.</Text>
        </Box>

        <Stepper active={active} color="grapefruit" mb={32}>
          {steps.map((label) => (
            <Stepper.Step key={label} label={label} />
          ))}
        </Stepper>

        {active === 0 && (
          <Paper shadow="md" radius="lg" p="xl" bg="var(--sat-surface)">
            <Stack gap={24}>
              <Group gap={16}>
                <ThemeIcon size={56} radius="xl" style={{ background: 'var(--sat-red)' }}>
                  {isTutorOrGod ? <Users size={28} color="var(--mantine-color-white)" /> : <BookOpen size={28} color="var(--mantine-color-white)" />}
                </ThemeIcon>
                <Box>
                  <Title order={3} c="var(--sat-text)">
                    {isTutorOrGod ? 'Hello, Tutor!' : 'Hello, Student!'}
                  </Title>
                  <Text c="var(--sat-text-soft)" size="sm">
                    {isTutorOrGod
                      ? 'You can create rosters, assign practice sets, and track student progress.'
                      : 'You can practice SAT questions, learn vocabulary, and track your progress.'}
                  </Text>
                </Box>
              </Group>

              <Box style={{ background: 'var(--sat-bg)', borderRadius: 12, padding: '20px 24px' }}>
                <Text fw={600} c="var(--sat-text)" mb={12}>What you&apos;ll have access to:</Text>
                <Stack gap={8}>
                  {(isTutorOrGod
                    ? [
                        '📋 Create and manage student rosters',
                        '📝 Build custom assignments from the question bank',
                        '📊 Grade submissions and provide feedback',
                        '📈 Track individual student progress',
                      ]
                    : [
                        '📚 Daily vocabulary flashcards with spaced repetition',
                        '🎯 SAT practice questions across all domains',
                        '📊 Real-time progress tracking and mastery levels',
                        '⭐ XP system to track your learning streak',
                      ]
                  ).map((item) => (
                    <Text key={item} size="sm" c="var(--sat-text-subtle)">{item}</Text>
                  ))}
                </Stack>
              </Box>

              <Button size="lg" fullWidth color="grapefruit" onClick={() => setActive(1)}>
                Let&apos;s Go →
              </Button>
            </Stack>
          </Paper>
        )}

        {isStudent && active === 1 && (
          <Paper shadow="md" radius="lg" p="xl" bg="var(--sat-surface)">
            <Stack gap={20}>
              <Box>
                <Title order={3} c="var(--sat-text)">Your goals</Title>
                <Text size="sm" c="var(--sat-text-soft)" mt={4}>
                  Optional — target scores and test date help personalize your dashboard.
                </Text>
              </Box>

              <Group grow align="flex-start">
                <TextInput
                  label="Target score: Reading & Writing"
                  placeholder="e.g. 700"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  {...form.getInputProps('target_score_rw')}
                />
                <TextInput
                  label="Target score: Math"
                  placeholder="e.g. 750"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  {...form.getInputProps('target_score_math')}
                />
              </Group>

              <TextInput
                label="SAT test date (if applicable)"
                type="date"
                {...form.getInputProps('sat_test_date')}
              />

              <Group justify="space-between" mt={8}>
                <Button variant="subtle" c="var(--sat-text-soft)" onClick={skipGoals}>
                  Skip
                </Button>
                <Button color="grapefruit" loading={saving} onClick={handleGoalsSave}>
                  Save & Continue
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}

        {tourActive && (
          <Paper shadow="md" radius="lg" p="xl" bg="var(--sat-surface)">
            <Stack gap={20}>
              <Box>
                <Title order={3} c="var(--sat-text)">Quick tour</Title>
                <Text size="sm" c="var(--sat-text-soft)" mt={4}>Here&apos;s how to navigate the platform.</Text>
              </Box>

              <Stack gap={12}>
                {(isTutorOrGod
                  ? [
                      { icon: '📋', title: 'Rosters', desc: 'Go to My Rosters to add students by email or create accounts with username and password.' },
                      { icon: '📝', title: 'Assignments', desc: 'Create assignments from the question bank and assign to a roster.' },
                      { icon: '✅', title: 'Grading', desc: 'Review submitted work under Grade Assignments.' },
                    ]
                  : [
                      { icon: '📚', title: 'Vocabulary', desc: 'Start your daily flashcard session — 20 new words per day.' },
                      { icon: '🎯', title: 'Practice', desc: 'Self-directed SAT practice by domain or mastery.' },
                      { icon: '📊', title: 'Dashboard', desc: 'Track your XP, mastery progress, and assignment scores.' },
                    ]
                ).map(({ icon, title, desc }) => (
                  <Box key={title} style={{ background: 'var(--sat-bg)', borderRadius: 10, padding: '14px 18px' }}>
                    <Group gap={12}>
                      <Text size="xl">{icon}</Text>
                      <Box>
                        <Text fw={600} size="sm" c="var(--sat-text)">{title}</Text>
                        <Text size="xs" c="var(--sat-text-subtle)">{desc}</Text>
                      </Box>
                    </Group>
                  </Box>
                ))}
              </Stack>

              <Button
                size="lg"
                fullWidth
                color="eveningSea"
                onClick={() => setActive(isTutorOrGod ? 2 : 3)}
              >
                Got It →
              </Button>
            </Stack>
          </Paper>
        )}

        {readyActive && (
          <Paper shadow="md" radius="lg" p="xl" bg="var(--sat-surface)" style={{ textAlign: 'center' }}>
            <Stack gap={20} align="center">
              <ThemeIcon size={72} radius="xl" style={{ background: 'var(--sat-green)' }}>
                <CheckCircle size={36} color="var(--mantine-color-white)" />
              </ThemeIcon>
              <Box>
                <Title order={2} c="var(--sat-text)">You&apos;re all set!</Title>
                <Text c="var(--sat-text-soft)" mt={8}>
                  {isTutorOrGod
                    ? 'Head to your dashboard to create your first roster.'
                    : 'Head to your dashboard and start learning.'}
                </Text>
              </Box>
              <Button
                size="lg"
                color="grapefruit"
                style={{ minWidth: 200 }}
                leftSection={<Target size={18} />}
                loading={finishing}
                onClick={handleFinish}
              >
                Go to Dashboard
              </Button>
            </Stack>
          </Paper>
        )}
      </Container>
    </Box>
  )
}
